import { useEffect, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { ContactShadows, Environment } from '@react-three/drei';
import * as THREE from 'three';
import { N, ORBIT_R, ANGLE_STEP, nodeAngle } from './world';
import { ChapterNode } from './buildings';

function CameraRig({ worldRef }) {
    const look = useRef(new THREE.Vector3(0, 1.6, 0));
    const mouse = useRef({ x: 0, y: 0 });
    const desired = useRef(new THREE.Vector3(0, 7.5, 17));
    const tmp = useRef(new THREE.Vector3());

    useEffect(() => {
        const onMove = (e) => {
            mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
            mouse.current.y = -((e.clientY / window.innerHeight) * 2 - 1);
        };
        window.addEventListener('mousemove', onMove);
        return () => window.removeEventListener('mousemove', onMove);
    }, []);

    useFrame((state, dt) => {
        const p = worldRef.current?.p ?? 0;
        const settle = THREE.MathUtils.clamp(p * 5, 0, 1);
        tmp.current
            .set(0, 7.5, 17)
            .lerp(new THREE.Vector3(0, 5.2, 13.2), settle)
            .add(new THREE.Vector3(mouse.current.x * 0.5, mouse.current.y * 0.3, 0));
        const k = 1 - Math.pow(0.002, dt);
        state.camera.position.lerp(tmp.current, k);
        look.current.lerp(new THREE.Vector3(mouse.current.x * 0.4, 1.6, 0), k);
        state.camera.lookAt(look.current);
        const aspect = state.size.width / state.size.height;
        const short = state.size.height < 720;
        const fov = aspect < 0.8 ? 46 : short ? 40 : 34;
        if (state.camera.fov !== fov) {
            state.camera.fov = fov;
            state.camera.updateProjectionMatrix();
        }
    });
    return null;
}

function Earth({ worldRef }) {
    const g = useRef(null);
    const spin = useRef(null);
    const base = useRef(null);
    const landRefs = useRef([]);
    const cloudRefs = useRef([]);
    const techRefs = useRef([]);

    const baseCols = [
        new THREE.Color('#3b8fd9'),
        new THREE.Color('#8a744c'),
        new THREE.Color('#131c30'),
    ];
    const landCols = [
        new THREE.Color('#4ade80'),
        new THREE.Color('#d4a24f'),
        new THREE.Color('#1b2c49'),
    ];

    const LANDS = [
        [15, 10], [45, 70], [8, 130], [-25, 45], [-12, -75],
        [35, -125], [-38, 150], [2, -165], [55, -25], [-45, -30],
    ];
    const CLOUDS = [[35, 40], [5, 100], [-30, -60], [50, -140], [-15, 165]];

    const surf = ([lat, lon], r) => {
        const phi = (90 - lat) * (Math.PI / 180);
        const theta = (lon + 180) * (Math.PI / 180);
        return [r * Math.sin(phi) * Math.cos(theta), r * Math.cos(phi), r * Math.sin(phi) * Math.sin(theta)];
    };

    useFrame((_, dt) => {
        if (!g.current || !base.current) return;
        const p = worldRef.current?.p ?? 0;
        const grow = THREE.MathUtils.smoothstep(p, 0.0, 0.03);
        g.current.scale.setScalar(0.001 + grow * 0.999);
        const age = THREE.MathUtils.smoothstep(p, 0.15, 0.75);
        const tech = THREE.MathUtils.smoothstep(p, 0.55, 0.95);
        spin.current.rotation.y += dt * 0.14;
        base.current.color.copy(baseCols[0]).lerp(baseCols[1], age).lerp(baseCols[2], tech * 0.85);
        landRefs.current.forEach((m) => {
            if (!m) return;
            m.color.copy(landCols[0]).lerp(landCols[1], age).lerp(landCols[2], tech * 0.9);
            m.emissiveIntensity = tech * 0.45;
        });
        cloudRefs.current.children.forEach((m) => {
            if (m) m.material.opacity = 0.35 * (1 - tech);
        });
        techRefs.current.forEach((m) => {
            if (!m) return;
            const s = 0.001 + tech;
            m.scale.setScalar(s);
        });
    });

    return (
        <group ref={g} position={[0, 1.6, 0]}>
            <group ref={spin}>
                <mesh castShadow>
                    <sphereGeometry args={[2.6, 48, 48]} />
                    <meshPhysicalMaterial
                        ref={base}
                        color="#3b8fd9"
                        roughness={0.36}
                        metalness={0.05}
                        clearcoat={1}
                        clearcoatRoughness={0.16}
                        envMapIntensity={0.9}
                    />
                </mesh>
                {LANDS.map(([lat, lon], i) => (
                    <mesh key={i} position={surf([lat, lon], 2.52)} scale={[1, 0.4, 1]} rotation={[lat / 90, lon / 90, 0]} castShadow>
                        <sphereGeometry args={[0.85, 20, 20]} />
                        <meshStandardMaterial
                            ref={(el) => (landRefs.current[i] = el)}
                            color="#4ade80"
                            emissive="#00F2FE"
                            emissiveIntensity={0}
                            roughness={0.55}
                            metalness={0.15}
                            flatShading
                        />
                    </mesh>
                ))}
                <group ref={cloudRefs}>
                    {CLOUDS.map(([lat, lon], i) => (
                        <mesh key={i} position={surf([lat, lon], 2.68)} scale={[1.2, 0.28, 1.2]}>
                            <sphereGeometry args={[0.55, 16, 16]} />
                            <meshStandardMaterial color="#e8edf5" transparent opacity={0.35} roughness={0.9} metalness={0} />
                        </mesh>
                    ))}
                </group>
            </group>
            <mesh scale={1.14}>
                <sphereGeometry args={[2.6, 32, 32]} />
                <meshBasicMaterial color="#3b82c4" transparent opacity={0.12} side={THREE.BackSide} />
            </mesh>
            <group rotation={[0.45, 0, 0.18]}>
                <mesh ref={(el) => (techRefs.current[9] = el)} scale={0.001} castShadow>
                    <torusGeometry args={[3.5, 0.04, 12, 64]} />
                    <meshStandardMaterial color="#8b95a8" metalness={0.95} roughness={0.2} emissive="#00F2FE" emissiveIntensity={0.5} />
                </mesh>
                <mesh ref={(el) => (techRefs.current[10] = el)} scale={0.001}>
                    <icosahedronGeometry args={[2.85, 1]} />
                    <meshBasicMaterial color="#00F2FE" wireframe transparent opacity={0.22} />
                </mesh>
                {Array.from({ length: 8 }).map((_, i) => {
                    const a = (i / 8) * Math.PI * 2;
                    return (
                        <mesh
                            key={`c${i}`}
                            ref={(el) => (techRefs.current[11 + i] = el)}
                            position={[Math.cos(a) * 3.5, 0, Math.sin(a) * 3.5]}
                            scale={0.001}
                            castShadow
                        >
                            <boxGeometry args={[0.22, 0.22, 0.22]} />
                            <meshStandardMaterial color="#F59E0B" emissive="#F59E0B" emissiveIntensity={1.4} metalness={0.6} roughness={0.3} />
                        </mesh>
                    );
                })}
            </group>
            <pointLight position={[3, 2, 4]} intensity={30} distance={14} color="#9be8ff" />
        </group>
    );
}

function Carousel({ worldRef, activeChapter, chapters }) {
    const g = useRef(null);

    useFrame((_, dt) => {
        if (!g.current) return;
        const p = worldRef.current?.p ?? 0;
        const grow = THREE.MathUtils.smoothstep(p, 0.0, 0.03);
        g.current.scale.setScalar(0.001 + grow * 0.999);
        const f = THREE.MathUtils.clamp(p * N - 0.5, 0, N - 1);
        const target = f * ANGLE_STEP - Math.PI / 2;
        g.current.rotation.y += (target - g.current.rotation.y) * (1 - Math.pow(0.002, dt));
    });

    return (
        <group ref={g}>
            <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
                <torusGeometry args={[ORBIT_R, 0.035, 12, 96]} />
                <meshStandardMaterial color="#8b95a8" metalness={0.95} roughness={0.22} emissive="#00F2FE" emissiveIntensity={0.25} />
            </mesh>
            {chapters.map((c, i) => {
                const theta = nodeAngle(i);
                const pos = [Math.cos(theta) * ORBIT_R, 0, Math.sin(theta) * ORBIT_R];
                return (
                    <ChapterNode
                        key={c.id}
                        index={i}
                        accent={c.accent}
                        active={activeChapter === i}
                        position={pos}
                        rotation-y={Math.PI / 2 - theta}
                    />
                );
            })}
        </group>
    );
}

function Scene({ worldRef, activeChapter, chapters }) {
    return (
        <>
            <fog attach="fog" args={['#08090C', 20, 58]} />
            <ambientLight intensity={0.65} color="#c5cdd9" />
            <directionalLight
                position={[0, 12, 12]}
                intensity={1.4}
                color="#9be8ff"
                castShadow
                shadow-mapSize-width={1024}
                shadow-mapSize-height={1024}
                shadow-camera-left={-13}
                shadow-camera-right={13}
                shadow-camera-top={13}
                shadow-camera-bottom={-13}
                shadow-camera-near={1}
                shadow-camera-far={45}
            />
            <directionalLight position={[-10, 6, -8]} intensity={0.4} color="#8B5CF6" />
            <Environment resolution={64} frames={1}>
                <mesh scale={60}>
                    <sphereGeometry args={[1, 32, 32]} />
                    <meshBasicMaterial color="#111827" side={THREE.BackSide} />
                </mesh>
                <mesh position={[0, 8, 8]} scale={5}>
                    <sphereGeometry args={[1, 16, 16]} />
                    <meshBasicMaterial color="#9be8ff" />
                </mesh>
                <mesh position={[-8, 2, -5]} scale={4}>
                    <sphereGeometry args={[1, 16, 16]} />
                    <meshBasicMaterial color="#8B5CF6" />
                </mesh>
                <mesh position={[6, -3, 6]} scale={4}>
                    <sphereGeometry args={[1, 16, 16]} />
                    <meshBasicMaterial color="#F59E0B" />
                </mesh>
            </Environment>
            <CameraRig worldRef={worldRef} />
            <Earth worldRef={worldRef} />
            <Carousel worldRef={worldRef} activeChapter={activeChapter} chapters={chapters} />
            <ContactShadows position={[0, 0.02, 0]} scale={30} opacity={0.5} blur={2.2} far={5} resolution={512} color="#000000" />
        </>
    );
}

export default function WorldCanvas({ worldRef, activeChapter, chapters }) {
    return (
        <div className="pointer-events-none fixed inset-0 z-[1]" data-testid="world-canvas">
            <Canvas
                shadows
                gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
                dpr={[1, 1.75]}
                camera={{ fov: 34, position: [0, 7.5, 17], near: 0.1, far: 140 }}
                style={{ position: 'absolute', inset: 0, background: 'transparent' }}
            >
                <Scene worldRef={worldRef} activeChapter={activeChapter} chapters={chapters} />
            </Canvas>
        </div>
    );
}
